import { useSeo } from '../hooks/useSeo';

export function TermsOfService() {
  useSeo('Terms of Service — Media Magic');
  return (
    <div className="portal-surface" style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
      <h1 style={{ fontSize: 32, marginBottom: 8, color: 'var(--ink-950)' }}>Terms of Service</h1>
      <p style={{ color: 'var(--ink-500)', marginBottom: 32, fontSize: 14 }}>
        Last updated: March 2026 | Effective Date: March 2026
      </p>

      <section style={styles.section}>
        <h2 style={styles.h2}>1. Acceptance of Terms & Agreement Scope</h2>
        <p style={styles.p}>
          By accessing, browsing, or using Media Magic's website, applications, services, SDKs, APIs, or any features (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to all terms and conditions, you must discontinue use immediately. Your continued use constitutes acceptance of these Terms.
        </p>
        <p style={styles.p}>
          These Terms apply to all users, including individual developers, teams, businesses, and enterprises. If you use the Service on behalf of an organization, you represent and warrant that you have authority to bind that organization to these Terms.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>2. Service Description & License Grant</h2>
        <p style={styles.p}>
          Media Magic provides serverless image optimization and transformation services, including SDKs, APIs, and web-based tools to optimize and deliver images in multiple formats and sizes. We grant you a limited, non-exclusive, non-transferable license to use the Service for your lawful purposes, subject to these Terms.
        </p>
        <p style={styles.p}>
          <strong>You may not:</strong>
        </p>
        <ul style={styles.list}>
          <li>Modify, copy, or create derivative works of the Service, SDK, or API code</li>
          <li>Use the Service for unlawful purposes or in violation of these Terms</li>
          <li>Attempt to decompile, reverse engineer, disassemble, or otherwise extract source code from Media Magic software</li>
          <li>Remove, alter, or obscure any copyright, trademark, or proprietary notices</li>
          <li>Sublicense, resell, or transfer the Service to third parties without written authorization</li>
          <li>Use the Service to process or transform images for purposes other than your own internal use or end-user delivery (without commercial resale)</li>
          <li>Circumvent rate limits, usage quotas, or payment requirements</li>
          <li>Access the Service through automated scripts, bots, or unauthorized API methods</li>
          <li>Mirror, cache, or redistribute Service content or APIs for competitive purposes</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>3. User Accounts & Registration</h2>

        <h3 style={styles.h3}>3.1 Account Creation</h3>
        <p style={styles.p}>
          To use certain features, you must create an account with accurate, current information. You are responsible for maintaining the confidentiality of your password and account credentials. You are liable for all activities occurring under your account. You must notify us immediately of any unauthorized access.
        </p>

        <h3 style={styles.h3}>3.2 Account Termination</h3>
        <p style={styles.p}>
          We may suspend or terminate your account if you: (a) violate these Terms, (b) engage in abusive behavior, (c) fail to pay charges, or (d) use the Service unlawfully. You may delete your account anytime through the dashboard. Upon termination, your account access ceases, though we may retain data as required by law or for legitimate business purposes.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>4. User Responsibilities & Prohibited Conduct</h2>

        <h3 style={styles.h3}>4.1 Your Obligations</h3>
        <p style={styles.p}>
          You represent and warrant that: (a) you own or have the right to process images you submit, (b) images don't infringe third-party rights, (c) you'll comply with all laws, (d) you'll comply with these Terms, and (e) you have obtained necessary consents from any people depicted in images.
        </p>

        <h3 style={styles.h3}>4.2 Prohibited Activities</h3>
        <p style={styles.p}>
          You may not:
        </p>
        <ul style={styles.list}>
          <li>Upload, submit, or process images that are unlawful, harmful, or infringe intellectual property rights</li>
          <li>Use the Service for illegal purposes or in violation of any law</li>
          <li>Harass, abuse, threaten, or defame others</li>
          <li>Interfere with the Service's operation or attempt unauthorized access</li>
          <li>Probe, scan, or test the Service's security vulnerabilities</li>
          <li>Exploit the Service for competitive intelligence or benchmarking</li>
          <li>Access the Service through automated scripts, bots, or other automated means without authorization</li>
          <li>Exceed rate limits or usage quotas, or attempt to circumvent billing</li>
          <li>Use the Service to process images for commercial resale or redistribution without appropriate licensing</li>
          <li>Submit images containing malware, spyware, or other malicious code</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>5. Intellectual Property Rights</h2>

        <h3 style={styles.h3}>5.1 Our IP</h3>
        <p style={styles.p}>
          All Service content—including software, code, APIs, SDKs, documentation, design, logos, and trademarks—is owned by Media Magic or its licensors and protected by copyright and intellectual property law. You have no rights except as expressly granted in this agreement.
        </p>

        <h3 style={styles.h3}>5.2 Your Content Rights</h3>
        <p style={styles.p}>
          You retain ownership of images and content you submit. By submitting images, you grant Media Magic a worldwide, non-exclusive license to: store, process, transform, cache, and deliver optimized versions for your purposes. We may use aggregated, non-identifying data about your usage for product improvement.
        </p>

        <h3 style={styles.h3}>5.3 Feedback & Suggestions</h3>
        <p style={styles.p}>
          Any feedback, suggestions, or ideas you provide about the Service may be used by Media Magic without obligation or compensation.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>6. Service Features & Limitations</h2>

        <h3 style={styles.h3}>6.1 Service Description</h3>
        <p style={styles.p}>
          Media Magic optimizes images by converting them to multiple formats (WebP, AVIF, etc.), resizing for different viewports, and caching for fast delivery. Specific features, formats supported, and processing limits depend on your account tier.
        </p>

        <h3 style={styles.h3}>6.2 Rate Limits & Quotas</h3>
        <p style={styles.p}>
          We enforce rate limits and usage quotas based on your plan. Exceeding limits may result in service degradation, throttling, or suspension. We reserve the right to change rate limits with 30 days' notice.
        </p>

        <h3 style={styles.h3}>6.3 Service Availability & Uptime</h3>
        <p style={styles.p}>
          While we strive for high availability, we do not guarantee uninterrupted or error-free service. We may perform maintenance, upgrades, or suspend service to prevent harm, with reasonable notice when possible. For Enterprise plans, uptime SLAs are specified in your agreement.
        </p>

        <h3 style={styles.h3}>6.4 Beta Features</h3>
        <p style={styles.p}>
          Some features may be labeled "beta," "experimental," or "in preview." These are provided AS-IS without warranties and may change or be discontinued without notice. Data from beta features may not be preserved if the feature discontinues.
        </p>

        <h3 style={styles.h3}>6.5 Free Tier Disclaimer</h3>
        <p style={styles.p}>
          Free accounts have reduced features, rate limits, and storage. We may modify, suspend, or discontinue free service at any time. We do not guarantee availability or performance for free accounts.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>7. Warranties Disclaimer</h2>
        <p style={styles.p}>
          <strong>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.</strong> MEDIA MAGIC DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, COMPLETENESS, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT:
        </p>
        <ul style={styles.list}>
          <li>The Service will meet your requirements</li>
          <li>The Service will be uninterrupted, secure, or error-free</li>
          <li>Optimization results will be acceptable or consistent</li>
          <li>Bugs or errors will be fixed</li>
          <li>Images will be delivered without loss or corruption</li>
        </ul>
        <p style={styles.p}>
          You assume all responsibility for the outcome of using the Service. This disclaimer applies to the maximum extent permitted by law.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>8. Limitation of Liability</h2>
        <p style={styles.p}>
          <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
        </p>
        <p style={styles.p}>
          (a) <strong>Direct Damages:</strong> Media Magic's total liability for direct damages shall not exceed the amount you paid to Media Magic in the 12 months preceding the claim.
        </p>
        <p style={styles.p}>
          (b) <strong>Indirect Damages:</strong> MEDIA MAGIC SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, REVENUE, BUSINESS OPPORTUNITY, OR GOODWILL, EVEN IF ADVISED OF THE POSSIBILITY THEREOF.
        </p>
        <p style={styles.p}>
          (c) <strong>Exceptions:</strong> This limitation does not apply to: indemnification obligations, breach of confidentiality, misappropriation of IP, gross negligence, or fraud.
        </p>
        <p style={styles.p}>
          Some jurisdictions do not allow limitation of liability, so this provision may not apply fully to you.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>9. Indemnification</h2>
        <p style={styles.p}>
          You agree to indemnify, defend, and hold harmless Media Magic and its officers, employees, agents, and affiliates from any claims, damages, losses, costs, and expenses (including attorney's fees) arising from:
        </p>
        <ul style={styles.list}>
          <li>Your violation of these Terms</li>
          <li>Your misuse of the Service</li>
          <li>Images you submit (infringement, defamation, privacy violations)</li>
          <li>Your violation of any law or third-party rights</li>
          <li>Your use of the Service in an unlawful or unauthorized manner</li>
        </ul>
        <p style={styles.p}>
          We will notify you of claims; you will defend us and cooperate reasonably.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>10. Payments, Billing & Refunds</h2>

        <h3 style={styles.h3}>10.1 Pricing</h3>
        <p style={styles.p}>
          Pricing is displayed in your dashboard and invoice. We reserve the right to change pricing with 30 days' notice. Price changes apply to renewals, not current billing periods.
        </p>

        <h3 style={styles.h3}>10.2 Billing</h3>
        <p style={styles.p}>
          You authorize Media Magic to charge your payment method. If a charge fails, you are responsible for updating payment information. We may suspend your account for non-payment.
        </p>

        <h3 style={styles.h3}>10.3 Refunds</h3>
        <p style={styles.p}>
          Refunds are at Media Magic's discretion. Monthly subscriptions are non-refundable except in cases of service failure caused by Media Magic. Requests must be made within 30 days of payment.
        </p>

        <h3 style={styles.h3}>10.4 Taxes</h3>
        <p style={styles.p}>
          You are responsible for all taxes applicable to your use. If you are tax-exempt, provide documentation.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>11. Third-Party Links & Services</h2>
        <p style={styles.p}>
          The Service may link to third-party websites, SDKs, or services. We are not responsible for their content, privacy practices, or terms. Your use of third parties is at your own risk and governed by their terms. We do not endorse third parties or guarantees about them.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>12. Changes to Terms & Service</h2>
        <p style={styles.p}>
          We may update these Terms or Service features at any time. We will provide reasonable notice (at least 30 days) for material changes by email or dashboard announcement. Continued use after changes constitutes acceptance. If you disagree, you must stop using the Service.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>13. Governing Law & Jurisdiction</h2>
        <p style={styles.p}>
          These Terms are governed by the laws of the United States of America, without regard to conflict of laws. You agree to the exclusive jurisdiction of courts located in the United States. Some jurisdictions may not permit exclusive jurisdiction clauses; applicable local law may override this.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>14. Dispute Resolution & Arbitration</h2>
        <p style={styles.p}>
          Before litigation, you agree to attempt resolving disputes through good-faith negotiation. If unresolved within 30 days, disputes shall be resolved through binding arbitration or litigation per applicable law. You waive the right to class action or jury trial.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>15. Entire Agreement & Severability</h2>
        <p style={styles.p}>
          These Terms (including Privacy Policy) constitute the entire agreement and supersede prior negotiations. If any provision is invalid or unenforceable, it shall be modified to the minimum extent to become valid, or severed without affecting other provisions.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>16. Contact Information</h2>
        <p style={styles.p}>
          For questions or disputes regarding these Terms, contact us at:
        </p>
        <p style={styles.p}>
          <strong>Email:</strong> support@media-magic.umangshah.me<br />
          <strong>Company:</strong> Media Magic<br />
          <strong>Response Time:</strong> We aim to respond within 10 business days
        </p>
      </section>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: '1px solid var(--line-soft)',
  },
  h2: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 12,
    color: 'var(--ink-950)',
  },
  h3: {
    fontSize: 16,
    fontWeight: 600,
    marginTop: 16,
    marginBottom: 8,
    color: 'var(--ink-950)',
  },
  p: {
    color: 'var(--ink-600)',
    lineHeight: 1.7,
    marginBottom: 12,
  },
  list: {
    marginLeft: 24,
    marginBottom: 12,
    color: 'var(--ink-600)',
    lineHeight: 1.7,
  },
};

export default TermsOfService;
